<template>
  <div>
    <v-row dense>
      <v-col
        cols="12"
        sm="12"
        md="12"
      >
        <v-combobox
          v-model="filters.affiliate"
          class="ma-3"
          dense
          clearable
          hide-details
          :items="affiliateOptions"
          :label="$i18n.t('$l.api.apiUserAffiliate')"
        />
      </v-col>
      <v-col
        cols="12"
        sm="12"
        md="6"
      >
        <v-menu
          ref="dateFromMenu"
          v-model="dateFromMenu"
          :close-on-content-click="false"
          :return-value.sync="filters.dateFrom"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filters.dateFrom"
              :label="$i18n.t('$l.api.subjects.checksFrom')"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              dense
              hide-details
              class="ma-3"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="filters.dateFrom"
            no-title
            scrollable
            :locale="$i18n.locale"
          >
            <v-spacer />
            <v-btn
              text
              color="primary"
              @click="dateFromMenuCancel"
            >
              {{ $t('$l.app.cancel') }}
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="dateFromMenuOk"
            >
              OK
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col
        cols="12"
        sm="12"
        md="6"
      >
        <v-menu
          ref="dateToMenu"
          v-model="dateToMenu"
          :close-on-content-click="false"
          :return-value.sync="filters.dateTo"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filters.dateTo"
              :label="$i18n.t('$l.api.subjects.checksTo')"
              prepend-icon="mdi-calendar"
              readonly
              hide-details
              v-bind="attrs"
              dense
              class="ma-3"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="filters.dateTo"
            no-title
            scrollable
            :locale="$i18n.locale"
          >
            <v-spacer />
            <v-btn
              text
              color="primary"
              @click="dateToMenuCancel"
            >
              {{ $t('$l.app.cancel') }}
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="dateToMenuOk"
            >
              OK
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <div
      class="greyee pa-3"
    >
      <basic-resp-row
        left="$l.api.subjects.totalChecks"
        :right="String(filteredItems.length)"
        width="100%"
        rowcss="body-2 ma-0 mb-2 info--text"
      />
      <basic-resp-row
        v-if="filteredWarning"
        left="$l.api.subjects.warnings.FILTEREDTOTALSUM"
        :right="String(filteredSpent)"
        width="100%"
        :rowcss="filteredWarning.threshold > filteredSpent ? 'body-2 ma-0 success--text' : 'body-2 ma-0 error--text'"
      />
      <v-row
        v-if="!filteredWarning"
        class="ma-0 pa-0 pb-1 info--text"
      >
        {{ $t(`$l.api.subjects.warnings.FILTEREDTOTALSUM`) }}
        <v-col class="ma-0 pa-0 text-end">
          <v-btn
            text
            x-small
            outlined
            color="info2"
            @click="initializeFilteredTotalSum(filteredWarning)"
          >
            {{ $t('$l.app.create') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <v-data-table
      dense
      :mobile-breakpoint="0"
      :items="filteredItems"
      :headers="headers"
      :options.sync="opts"
      :sort-by="'created'"
      :footer-props="{ itemsPerPageOptions: [10] }"
    >
      <template #[`item.created`]="{ item }">
        <span style="white-space: nowrap; font-size: 12px; font-weight: 500">{{ $utils.formatDate(new Date(item.created + ' UTC').toISOString()) }}</span>
      </template>
      <template #[`item.checkStatus`]="{ item }">
        <span
          v-if="item.checkStatus === 'NEW_OK'"
          style="white-space: nowrap"
        >
          <v-icon color="success">mdi-check-bold</v-icon>
        </span>
        <span
          v-if="item.checkStatus === 'NEW_MATCH'"
          style="white-space: nowrap"
        >
          <v-icon color="warning">mdi-alert-box-outline</v-icon>
        </span>
        <span
          v-if="item.checkStatus === 'NEW_UNSEARCHABLE'"
          style="white-space: nowrap"
        >
          <v-icon color="warning">mdi-close-thick</v-icon>
        </span>
        <span
          v-if="item.checkStatus === 'NEW_FAILED'"
          style="white-space: nowrap"
        >
          <v-icon color="info">mdi-close-thick</v-icon>
        </span>
        <span
          v-if="item.checkStatus === 'CONFIRMED_OK'"
          style="white-space: success"
        >
          <v-icon color="success">mdi-close-thick</v-icon>
        </span>
        <span
          v-if="item.checkStatus === 'CONFIRMED_MATCH'"
          style="white-space: nowrap"
        >
          <v-icon color="error">mdi-close-thick</v-icon>
        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script>

export default {
  name: 'SubjectCardPart3',
  props: {
    partData: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      opts: null,
      dateFromMenu: false,
      dateToMenu: false,
      filters: {
        dateFrom: '',
        dateTo: '',
        affiliate: ''
      }
    }
  },
  computed: {
    items () {
      const checks = JSON.parse(this.partData.transientData).checks
      return Object.values(checks)
    },

    filteredSpent () {
      return this.filteredItems.reduce((sum, item) => {
        // Make sure the value is numeric
        const spent = parseFloat(item.subjectSpentAmount) || 0
        return sum + spent
      }, 0)
    },

    filteredWarning () {
      const subjectWarnings = JSON.parse(this.partData.data).subjectWarnings
      return Object.values(subjectWarnings).find(w => w.name === 'FILTEREDTOTALSUM')
    },

    // List of affiliate choices extracted from data
    affiliateOptions () {
      const set = new Set()
      this.items.forEach(i => {
        if (i.apiUserAffiliate) set.add(i.apiUserAffiliate)
      })
      return Array.from(set).sort()
    },

    // MAIN FILTERED RESULT
    filteredItems () {
      return this.items.filter(item => {
        // --- date filtering ---
        if (this.filters.dateFrom) {
          const from = new Date(this.filters.dateFrom)
          const created = new Date(item.created + ' UTC')
          if (created < from) return false
        }

        if (this.filters.dateTo) {
          const to = new Date(this.filters.dateTo)
          const created = new Date(item.created + ' UTC')
          // end of day fix
          to.setHours(23, 59, 59, 999)
          if (created > to) return false
        }

        // --- affiliate filtering ---
        if (this.filters.affiliate) {
          if (item.apiUserAffiliate !== this.filters.affiliate) return false
        }

        return true
      })
    },

    headers () {
      return [
        { text: this.$t('Created'), value: 'created', sortable: true, sort: (a, b) => this.getCreatedSort(a, b) },
        { text: this.$t('Status'), value: 'checkStatus', sortable: true },
        { text: this.$t('Spent'), value: 'subjectSpentAmount', sortable: true },
        { text: this.$t('Aff'), value: 'apiUserAffiliate', sortable: true },
        { text: this.$t('User'), value: 'apiUserName', sortable: true },
        { text: this.$t('ID'), value: 'subjectId', sortable: true },
        { text: this.$t('CID'), value: 'cardId', sortable: true },
        { text: this.$t('Cntr'), value: 'country', sortable: true },
        { text: this.$t('CntrRisk'), value: 'countryRiskLevel', sortable: true },
        { text: this.$t('PEPM'), value: 'pepMatches', sortable: true },
        // { text: this.$t('PEPUn'), value: 'pepUnsearchable', sortable: false },
        { text: this.$t('SancM'), value: 'sanctionMatches', sortable: true },
        // { text: this.$t('SancUn'), value: 'sanctionUnsearchable', sortable: false },
        { text: this.$t('CheckOK'), value: 'checkFinished', sortable: true }
      ]
    }
  },
  methods: {
    getCreatedSort (a, b) {
      const desc = !this.opts.sortDesc[0]
      return (a.id > b.id) ? (desc ? 1 : -1) : (desc ? -1 : 1)
    },
    dateFromMenuOk () {
      if (this.$refs.dateFromMenu) {
        this.$refs.dateFromMenu.save(this.filters.dateFrom)
      }
      this.dateFromMenu = false
    },
    dateFromMenuCancel () {
      if (this.$refs.dateFromMenu) {
        this.filters.dateFrom = ''
        this.$refs.dateFromMenu.save('')
      }
      this.dateFromMenu = false
    },
    dateToMenuOk () {
      if (this.$refs.dateToMenu) {
        this.$refs.dateToMenu.save(this.filters.dateTo)
      }
      this.dateToMenu = false
    },
    dateToMenuCancel () {
      if (this.$refs.dateToMenu) {
        this.filters.dateTo = ''
        this.$refs.dateToMenu.save('')
      }
      this.dateToMenu = false
    },
    initializeFilteredTotalSum (filteredWarning) {
      let updateRequest = {
        subjectId: this.partData.id,
        subjectWarning: filteredWarning
      }
      this.$xapi.post('apiSubject/updateSubjectWarnings', updateRequest)
        .then(r => {
          this.$emit('subjectListParamsChanged', this.partData.id)
        })
    }
  }
}
</script>
